import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Save, Loader2, Bot, Clock, Mail, Play, CheckCircle2, AlertCircle, History,
} from 'lucide-react';
import { settingsApi, titanApi } from '../../lib/api';
import type { WarRoomSettings, WarRoomAuditSummaryRow } from '../../types/titan';

const DEFAULT: WarRoomSettings = {
  strategy_creation_time: '03:45',
  email_summary_enabled: true,
  email_recipients: '',
  auto_apply_strategy: true,
  default_allocations: { stocks: 1.0, crypto: 0.0, forex: 0.0, futures: 0.0 },
  daily_budget_pct: 0.8,
  profit_target_pct: 0.015,
  war_room_stocks_only: true,
};

export default function WarRoomSettingsTab() {
  const [settings, setSettings] = useState<WarRoomSettings>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [history, setHistory] = useState<WarRoomAuditSummaryRow[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsData, auditData] = await Promise.allSettled([
        settingsApi.warRoom(),
        titanApi.warRoomAuditList({ limit: 7 }),
      ]);
      if (settingsData.status === 'fulfilled' && settingsData.value?.settings) {
        setSettings({ ...DEFAULT, ...settingsData.value.settings });
      }
      if (auditData.status === 'fulfilled') {
        setHistory(auditData.value?.audits ?? []);
      }
    } catch {
      setError('Failed to load War Room settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    setError('');
    try {
      await settingsApi.saveWarRoom(settings);
      setMsg('War Room settings saved.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await settingsApi.testWarRoomEmail();
      setTestResult({ ok: true, text: `Test email sent to ${settings.email_recipients}` });
    } catch (e: any) {
      setTestResult({ ok: false, text: e?.message || 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-muted-foreground py-8">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading War Room settings…
    </div>
  );

  const lastRunDate = settings.last_ai_allocation_date;
  const lastBias = settings.last_ai_bias;
  const lastEquity = settings.last_account_equity;

  return (
    <div className="space-y-6">
      {/* Last run status */}
      {lastRunDate && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <span className="font-medium">Last run:</span>
              <Badge variant="outline">{lastRunDate}</Badge>
              {lastBias && <Badge variant="outline">Bias: {lastBias}</Badge>}
              {lastEquity && <Badge variant="outline">Equity: ${lastEquity.toLocaleString()}</Badge>}
              {settings.last_ai_daily_target_pct && (
                <Badge variant="outline">Target: {(settings.last_ai_daily_target_pct * 100).toFixed(1)}%</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Run history (last 7 days) */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4" /> Recent Run History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left py-1 pr-3">Date</th>
                    <th className="text-left py-1 pr-3">Bias</th>
                    <th className="text-left py-1 pr-3">Bots</th>
                    <th className="text-left py-1 pr-3">Target</th>
                    <th className="text-left py-1">Fleet</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.date} className="border-b border-muted/30 hover:bg-muted/20 transition-colors">
                      <td className="py-1.5 pr-3 font-mono">{row.date}</td>
                      <td className="py-1.5 pr-3 capitalize">{row.overall_bias ?? '—'}</td>
                      <td className="py-1.5 pr-3">
                        {row.bots_created_count != null
                          ? <Badge variant="outline" className="text-[10px]">{row.bots_created_count}</Badge>
                          : '—'}
                      </td>
                      <td className="py-1.5 pr-3">
                        {row.daily_target_pct != null
                          ? `${(row.daily_target_pct * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                      <td className="py-1.5">{row.fleet_mode ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4" /> Daily Schedule
          </CardTitle>
          <CardDescription>
            The War Room fires once per day at this time (ET) to analyse the market and deploy bots.
            Set this 10–15 minutes before your earliest trading session so bots are live when the session opens.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs">
            <label className="text-xs text-muted-foreground block mb-1">Fire time (ET)</label>
            <Input
              type="time"
              value={settings.strategy_creation_time}
              onChange={e => setSettings(s => ({ ...s, strategy_creation_time: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Recommended: <strong>03:30</strong> for 4 AM pre-market, <strong>06:00</strong> for regular-hours only.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Auto-apply strategy</p>
              <p className="text-xs text-muted-foreground">Automatically deploy bots after War Room completes. If off, you must approve manually.</p>
            </div>
            <Switch
              checked={settings.auto_apply_strategy}
              onCheckedChange={v => setSettings(s => ({ ...s, auto_apply_strategy: v }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Capital */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot className="w-4 h-4" /> Capital Allocation
          </CardTitle>
          <CardDescription>How much of your equity the War Room is allowed to put to work each day.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Daily budget (% of equity)</label>
            <Input
              type="number" min={10} max={100} step={5}
              value={((settings.daily_budget_pct ?? 0.8) * 100).toFixed(0)}
              onChange={e => setSettings(s => ({ ...s, daily_budget_pct: parseFloat(e.target.value) / 100 }))}
            />
            <p className="text-xs text-muted-foreground mt-1">Maximum capital deployable across all bots today.</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Profit target (% of equity)</label>
            <Input
              type="number" min={0.1} max={10} step={0.1}
              value={((settings.profit_target_pct ?? 0.015) * 100).toFixed(1)}
              onChange={e => setSettings(s => ({ ...s, profit_target_pct: parseFloat(e.target.value) / 100 }))}
            />
            <p className="text-xs text-muted-foreground mt-1">Daily P&L goal. Circuit breaker scales down once reached.</p>
          </div>
        </CardContent>
      </Card>

      {/* Email */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4" /> Email Summary
            </CardTitle>
            <Switch
              checked={settings.email_summary_enabled}
              onCheckedChange={v => setSettings(s => ({ ...s, email_summary_enabled: v }))}
            />
          </div>
          <CardDescription>Receive a daily War Room briefing email after each run.</CardDescription>
        </CardHeader>
        {settings.email_summary_enabled && (
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Recipients (comma-separated)</label>
              <Input
                type="email"
                value={settings.email_recipients}
                onChange={e => setSettings(s => ({ ...s, email_recipients: e.target.value }))}
                placeholder="you@example.com, partner@example.com"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={testEmail} disabled={testing || !settings.email_recipients}
                className="flex items-center gap-2">
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Send test email
              </Button>
              {testResult && (
                <span className={`text-sm flex items-center gap-1 ${testResult.ok ? 'text-green-600' : 'text-red-500'}`}>
                  {testResult.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {testResult.text}
                </span>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Save */}
      <div className="flex items-center gap-4">
        <Button onClick={save} disabled={saving} className="flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </Button>
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>
    </div>
  );
}
